import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Loader2,
  User,
  Briefcase,
  FileText,
  Search,
  TrendingUp,
  Calendar,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalApplications: 0,
    activeApplications: 0,
    profileCompleteness: 0,
    recentApplications: [],
  });
  const [candidateName, setCandidateName] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('Not authenticated');
        return;
      }

      const { data: candidate } = await supabase
        .from('candidates')
        .select('id, first_name, last_name, phone, location_city, citizenship_status, clearance_level, notice_period, payroll_type, onboarding_complete')
        .eq('user_id', user.id)
        .single();

      if (!candidate) {
        setError('Candidate profile not found');
        return;
      }

      if (candidate && !candidate.onboarding_complete) {
        navigate('/candidate/onboarding', { replace: true });
        return;
      }

      setCandidateName(candidate.first_name || 'there');

      // Fetch skills and files counts for completeness
      const [{ count: skillsCount }, { count: filesCount }] = await Promise.all([
        supabase.from('candidate_skills').select('id', { count: 'exact', head: true }).eq('candidate_id', candidate.id),
        supabase.from('candidate_files').select('id', { count: 'exact', head: true }).eq('candidate_id', candidate.id).eq('status', 'active'),
      ]);

      // Calculate profile completeness
      let completeness = 0;
      if (candidate.first_name) completeness += 10;
      if (candidate.last_name) completeness += 10;
      if (candidate.phone) completeness += 10;
      if (candidate.location_city) completeness += 10;
      if (candidate.citizenship_status) completeness += 10;
      if (candidate.clearance_level && candidate.clearance_level !== 'None') completeness += 10;
      if (skillsCount > 0) completeness += 15;
      if (filesCount > 0) completeness += 15;
      if (candidate.notice_period) completeness += 5;
      if (candidate.payroll_type) completeness += 5;

      // Fetch applications
      const { data: applications } = await supabase
        .from('applications')
        .select(`
          id,
          status,
          created_at,
          rfq:rfqs(title, ref_code, client:clients(name))
        `)
        .eq('candidate_id', candidate.id)
        .order('created_at', { ascending: false });

      const activeStatuses = ['submitted', 'reviewing', 'shortlisted', 'interviewing', 'offered'];
      const activeApps = applications?.filter(app => activeStatuses.includes(app.status)) || [];

      setStats({
        totalApplications: applications?.length || 0,
        activeApplications: activeApps.length,
        profileCompleteness: completeness,
        recentApplications: applications?.slice(0, 3) || [],
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
      case 'reviewing':
        return 'bg-blue-100 text-blue-800';
      case 'shortlisted':
        return 'bg-purple-100 text-purple-800';
      case 'interviewing':
        return 'bg-yellow-100 text-yellow-800';
      case 'offered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Welcome back, {candidateName}!</h1>
        <p className="text-muted-foreground mt-2 text-sm sm:text-base">
          Here's what's happening with your job search
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/candidate/applications')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalApplications}</div>
            <p className="text-xs text-muted-foreground mt-1">
              All time submissions
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/candidate/applications')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Applications</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeApplications}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Currently in progress
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/candidate/my-details')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profile Completeness</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.profileCompleteness}%</div>
            {stats.profileCompleteness < 100 && (
              <p className="text-xs text-muted-foreground mt-1">
                Complete your profile →
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks to get you started</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="h-auto flex flex-col items-start p-4 gap-2"
              onClick={() => navigate('/candidate/job-board')}
            >
              <Search className="h-5 w-5" />
              <div className="text-left">
                <p className="font-medium">Browse Jobs</p>
                <p className="text-xs text-muted-foreground">Find new opportunities</p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto flex flex-col items-start p-4 gap-2"
              onClick={() => navigate('/candidate/my-details')}
            >
              <Briefcase className="h-5 w-5" />
              <div className="text-left">
                <p className="font-medium">Update Resume</p>
                <p className="text-xs text-muted-foreground">Keep your resume current</p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto flex flex-col items-start p-4 gap-2"
              onClick={() => navigate('/candidate/applications')}
            >
              <FileText className="h-5 w-5" />
              <div className="text-left">
                <p className="font-medium">View Applications</p>
                <p className="text-xs text-muted-foreground">Track your progress</p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto flex flex-col items-start p-4 gap-2"
              onClick={() => navigate('/candidate/my-details')}
            >
              <User className="h-5 w-5" />
              <div className="text-left">
                <p className="font-medium">Edit Profile</p>
                <p className="text-xs text-muted-foreground">Update your details</p>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Applications */}
      {stats.recentApplications.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Applications</CardTitle>
                <CardDescription>Your latest submissions</CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/candidate/applications')}
              >
                View All →
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentApplications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer"
                  onClick={() => navigate('/candidate/applications')}
                >
                  <div className="flex-1">
                    <p className="font-medium">{app.rfq?.title || 'Unknown Position'}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      <Calendar className="h-3 w-3" />
                      Applied {new Date(app.created_at).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                    {app.status.replace('_', ' ').toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Applications CTA */}
      {stats.totalApplications === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Ready to start your job search?</h3>
            <p className="text-muted-foreground mb-4">
              Browse available positions and submit your first application
            </p>
            <Button onClick={() => navigate('/candidate/job-board')}>
              Browse Job Board
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CandidateDashboard;
